import React, { ReactNode, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  TextInput,
  TextInputProps,
  Image,
  View,
  Pressable,
  Platform,
  TextInputFocusEventData,
  NativeSyntheticEvent,
  ImageSourcePropType,
  StyleProp,
  TextStyle,
  ViewStyle,
} from 'react-native';
import useStyles from '../../hooks/styles-hook';
import IconClose from '../../../assets/icon-close.png';
import IconBack from '../../../assets/icon-back.png';
import useTranslation from '../../hooks/translation-hook';
import MaskInput, { Masks } from 'react-native-mask-input';
import FieldErrorMessage from './FieldErrorMessage';
import { getCanadianPhoneValidationState } from '../../helpers/phoneNumberHelper';

export interface ValidationResult {
  valid: boolean;
  message: string;
}

export enum InputTypeValidation {
  int,
  date,
  email,
  phone,
  luckyNumber,
}

interface CustomTextInputProps extends TextInputProps {
  containerStyle?: StyleProp<ViewStyle> | undefined;
  forcePlaceholderTextColor?: string;
  steadyIcon?: ReactNode;
  showClearIcon?: boolean;
  showBackIcon?: boolean;
  debounceBack?: number;
  onBack?(): void;
  onClear?(): void;

  required?: boolean;
  requiredMessage?: string;
  inputTypeValidation?: InputTypeValidation;
  digitsOnly?: boolean;
  maxLength?: number;
  minLength?: number;
  validateOnBlur?: boolean;
  onValidationChange?(validation: ValidationResult): void;
  forceValidation?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  showErrorMessage?: boolean;
  errorTextStyle?: StyleProp<TextStyle>;
}

let bounceBack = 0;
let bounceBackTimer: any = undefined;

function callChangeOnDebounce(onTextChange: (value: string) => void, value: string, skipDebounce: boolean = false) {
  clearTimeout(bounceBackTimer);

  if (skipDebounce || !bounceBack || bounceBack <= 0) {
    bubbleChangeEvent();
  } else {
    bounceBackTimer = window.setTimeout(() => {
      bubbleChangeEvent();
    }, bounceBack);
  }

  function bubbleChangeEvent() {
    if (onTextChange) {
      onTextChange(value);
    }
  }
}

const CustomTextInput = React.forwardRef((props: CustomTextInputProps, ref) => {
  const { digitsOnly, ...textInputProps } = props;
  const t = useTranslation();
  const styles = useStyles((section) => section.customTextInput);
  const initialValue = props.value ?? '';

  const inputText = useRef<TextInput>(null);

  const [validation, setValidation] = useState<ValidationResult>({ valid: true, message: undefined });
  const [value, setValue] = useState<string>();
  const [hasValue, setHasValue] = useState<boolean>(props.value ? true : false);
  const [dirty, setDirty] = useState(false);
  const [focus, setFocus] = useState(false);
  const hasInternalError = !!validation && !validation.valid;
  const resolvedErrorMessage = props.errorMessage ?? (hasInternalError ? validation?.message : undefined);
  const showErrorState = !!props.hasError || !!props.errorMessage || hasInternalError;
  const webInputResetStyle = Platform.OS === 'web' ? styles['input.webReset'] : undefined;
  const validateOnBlur = props.validateOnBlur ?? true;

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputText.current?.focus();
    },
    clear: () => {
      handleClearText();
    },
  }));

  bounceBack = props.debounceBack ?? 0;

  useEffect(() => {
    if (!validation) {
      return;
    }

    if (props.onValidationChange) {
      props.onValidationChange(validation);
    }
  }, [validation]);

  useEffect(() => {
    // Once an error is visible, revalidate edits/autofill immediately. Before
    // the first validation, retain the normal validate-on-blur behavior.
    if (!validateOnBlur || !validation.valid) {
      const shouldValidate = dirty || !validation.valid;
      if (validateFieldRequired(shouldValidate, !!props.value)) {
        if (validateFieldValueLength(shouldValidate, !!props.value, props.value) && props.inputTypeValidation !== undefined) {
          validateInputType(shouldValidate, !!props.value, props.value);
        }
      }
    }

    if (props.value) {
      setHasValue(true);
      setValue(props.value);
    } else {
      //if (props.value === '') {
      setHasValue(false);
      setValue('');
    }
  }, [props.value, validateOnBlur]);

  useEffect(() => {
    if (props.forceValidation) {
      if (validateFieldRequired(true, !!value)) {
        if (validateFieldValueLength(true, !!value, value) && props.inputTypeValidation !== undefined) {
          validateInputType(true, !!value, value);
        }
      }
    }
  }, [props.forceValidation]);

  const setValidationState = (nextValidation: ValidationResult) => {
    setValidation((currentValidation) => {
      if (currentValidation?.valid === nextValidation.valid && currentValidation?.message === nextValidation.message) {
        return currentValidation;
      }

      return nextValidation;
    });
  };

  const validateFieldRequired = (isDirty: boolean, hasValue: boolean): boolean => {
    if (props.required) {
      if (isDirty) {
        if (props.required && !hasValue) {
          setValidationState({
            valid: false,
            message: props.requiredMessage ?? t('field_required'),
          });
          return false;
        }
      } else {
        if (!validation || !validation.valid) {
          setValidationState({
            valid: true,
            message: undefined,
          });
        }
      }
    } else {
      setValidationState({
        valid: true,
        message: undefined,
      });
    }

    return true;
  };

  const validateFieldValueLength = (isDirty: boolean, hasValue: boolean, value: string): boolean => {
    if (props.inputTypeValidation === InputTypeValidation.phone) {
      setValidationState({
        valid: true,
        message: undefined,
      });
      return true;
    }

    if (props.minLength === undefined && props.maxLength === undefined) {
      setValidationState({
        valid: true,
        message: undefined,
      });
      return true;
    }

    if (isDirty && hasValue) {
      let validationResult: ValidationResult = { valid: true, message: '' };

      if (props.minLength) {
        if (value.trim().length < props.minLength) {
          validationResult.valid = false;
          validationResult.message = t('field_min_length', { minLength: props.minLength.toString() });

          setValidationState(validationResult);

          return false;
        }
      }

      if (props.maxLength) {
        if (value.trim().length > props.maxLength) {
          validationResult.valid = false;
          validationResult.message = t('field_max_length', { maxLength: props.maxLength.toString() });
          setValidationState(validationResult);

          return false;
        }
      }

      setValidationState(validationResult);

      return true;
    }
  };

  const validateInputType = (isDirty: boolean, hasValue: boolean, value: string): boolean => {
    if (isDirty && hasValue) {
      let validationResult: ValidationResult = { valid: true, message: '' };

      switch (props.inputTypeValidation) {
        case InputTypeValidation.int:
          if (!Number.isNaN(Number(value)) && value.indexOf('.') === -1) {
            validationResult.valid = true;
            validationResult.message = '';
          } else {
            validationResult.valid = false;
            validationResult.message = t('field_value_not_valid');
          }
          break;
        case InputTypeValidation.email:
          const regexp = new RegExp(
            /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
          );
          var isValidEmail: boolean;

          isValidEmail = regexp.test(value.trim());

          if (isValidEmail) {
            validationResult.valid = true;
            validationResult.message = '';
          } else {
            validationResult.valid = false;
            validationResult.message = t('Invalid-email-address');
          }
          break;
        case InputTypeValidation.date:
          const date: Date = new Date(value);

          //@ts-ignore
          if (date instanceof Date && !isNaN(date)) {
            validationResult.valid = true;
            validationResult.message = '';
          } else {
            validationResult.valid = false;
            validationResult.message = t('field_value_not_valid');
          }

          break;
        case InputTypeValidation.phone:
          const phoneValidation = getCanadianPhoneValidationState(value, t);

          validationResult.valid = phoneValidation.valid;
          validationResult.message = phoneValidation.message;
          break;
      }

      setValidationState(validationResult);

      return validationResult.valid;
    }
  };

  const handleTextChange = (text: string) => {
    const e = digitsOnly ? text.replace(/[^0-9]/g, '') : text;
    let newDirtyValue = false;
    let newHasValue = false;

    if (e.trim() !== initialValue.trim()) {
      newDirtyValue = true;
    } else {
      newDirtyValue = false;
    }

    if (e.trim() !== '') {
      newHasValue = true;
    } else {
      newHasValue = false;
    }

    if (!validateOnBlur || !validation.valid) {
      const shouldValidate = newDirtyValue || !validation.valid;
      if (validateFieldRequired(shouldValidate, newHasValue)) {
        if (validateFieldValueLength(shouldValidate, newHasValue, e) && props.inputTypeValidation !== undefined) {
          validateInputType(shouldValidate, newHasValue, e);
        }
      }
    }

    setHasValue(newHasValue);
    setDirty(newDirtyValue);
    setValue(e);

    callChangeOnDebounce(props.onChangeText, e);
  };

  const handleBlur = (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
    setFocus(false);

    if (validateOnBlur) {
      if (validateFieldRequired(true, !!value)) {
        if (validateFieldValueLength(true, !!value, value) && props.inputTypeValidation !== undefined) {
          validateInputType(true, !!value, value);
        }
      }
    }

    if (props.onBlur) {
      props.onBlur(e);
    }
  };

  const handleFocus = (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
    setFocus(true);

    if (props.onFocus) {
      props.onFocus(e);
    }
  };

  const handleClearText = () => {
    inputText.current.focus();
    setValue('');
    setFocus(true);
    setHasValue(false);
    setDirty(false);

    if (props.onClear) {
      props.onClear();
    }
  };

  const handleBack = () => {
    if (props.onBack) {
      props.onBack();
    }
  };

  const steadyIcon =
    props.steadyIcon && !focus && !hasValue ? (
      <View style={[styles.iconBase, styles.icon.steady]} focusable={false}>
        {props.steadyIcon}
      </View>
    ) : null;

  const backButton =
    props.showBackIcon && (focus || hasValue) ? (
      <Pressable style={[styles.iconBase, styles.icon.back]} onPress={handleBack} focusable={false}>
        <Image source={IconBack} style={styles.icon['back.dimension']} />
      </Pressable>
    ) : null;

  const clearButton =
    props.showClearIcon && hasValue ? (
      <Pressable style={[styles.iconBase, styles.icon.clear]} onPress={handleClearText} focusable={false}>
        <Image source={IconClose} style={styles.icon['clear.dimension']} />
      </Pressable>
    ) : null;

  let currentInputStyle: any = [styles.input];

  if (hasValue) {
    currentInputStyle = [styles.inputTouched];

    if (props.showClearIcon) {
      currentInputStyle.push(styles['inputTouched.clearIconPadding']);
    }
  }

  if (props.steadyIcon && !focus && !hasValue) {
    currentInputStyle.push(styles['inputTouched.steadyIconPadding']);
  }

  if ((focus || hasValue) && props.showBackIcon) {
    currentInputStyle.push(styles['inputTouched.backIconPadding']);
  }

  if (focus) {
    currentInputStyle.push(styles['input.focus']);
  }

  if (showErrorState) {
    currentInputStyle.push(styles['input.error']);
  }

  return (
    <View style={props.containerStyle ?? {}}>
      <View style={styles.container}>
        {backButton}
        {steadyIcon}
        {props.inputTypeValidation !== InputTypeValidation.date &&
          props.inputTypeValidation !== InputTypeValidation.phone &&
          props.inputTypeValidation !== InputTypeValidation.luckyNumber && (
            <TextInput
              {...textInputProps}
              style={[currentInputStyle, webInputResetStyle, props.style]}
              placeholderTextColor={props.forcePlaceholderTextColor ?? props.placeholderTextColor}
              onChangeText={handleTextChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              value={value}
              ref={inputText}
            />
          )}
        {props.inputTypeValidation === InputTypeValidation.date && (
          <MaskInput
            {...textInputProps}
            style={[currentInputStyle, webInputResetStyle, props.style]}
            placeholderTextColor={props.forcePlaceholderTextColor ?? props.placeholderTextColor}
            selectTextOnFocus={true}
            onFocus={handleFocus}
            onBlur={handleBlur}
            value={value}
            ref={inputText}
            maskAutoComplete={true}
            mask={Masks.DATE_YYYYMMDD}
            onChangeText={handleTextChange}
            keyboardType="numeric"
          />
        )}
        {props.inputTypeValidation === InputTypeValidation.luckyNumber && (
          <MaskInput
            {...textInputProps}
            style={[currentInputStyle, webInputResetStyle, props.style]}
            placeholderTextColor={props.forcePlaceholderTextColor ?? props.placeholderTextColor}
            selectTextOnFocus={true}
            onFocus={handleFocus}
            onBlur={handleBlur}
            value={value}
            ref={inputText}
            maskAutoComplete={true}
            mask={[/\d/, /\d/, /\d/]}
            onChangeText={handleTextChange}
            keyboardType="numeric"
          />
        )}
        {props.inputTypeValidation === InputTypeValidation.phone && (
          <MaskInput
            {...textInputProps}
            style={[currentInputStyle, webInputResetStyle, props.style]}
            placeholderTextColor={props.forcePlaceholderTextColor ?? props.placeholderTextColor}
            selectTextOnFocus={true}
            onFocus={handleFocus}
            onBlur={handleBlur}
            value={value}
            ref={inputText}
            maskAutoComplete={true}
            mask={Masks.USA_PHONE}
            onChangeText={handleTextChange}
            keyboardType="phone-pad"
          />
        )}
        {clearButton}
      </View>
      {props.showErrorMessage && !!resolvedErrorMessage && <FieldErrorMessage message={resolvedErrorMessage} style={props.errorTextStyle} />}
    </View>
  );
});

export default CustomTextInput;
